import styled from "styled-components";
import { useState, useEffect, useContext, useRef } from "react";
import { Dropdown } from "primereact/dropdown";
import OrgTreeSelect from "../../UI/OrgTreeSelect/OrgTreeSelect";
import {
  findNodeById,
  createAdminOrgTree,
} from "../../../Helpers/OrgTreeFunctions";
import { OrgTreeContext } from "../../../Contexts/OrgTreeContext";
import { CurrentUserContext } from "../../../Contexts/CurrentUserContext";

const Line = styled.div`
  margin: 10px 0;
`;

const RolesEditWrapper = styled.div`
  .add-role {
    font-size: 10px;
    margin-left: 5px;
    margin-top: 5px;
    color: green;
    cursor: pointer;
  }
  .add-email:hover {
    color: #005900;
  }
  .p-inputtext {
    margin: 2px 0 5px 0;
  }
  .p-inputtext,
  .p-treeselect,
  .p-inputwrapper {
    width: 270px;
  }
  i {
    margin: 0 8px;
  }
  .role-box-edit {
    display: flex;
    justify-content: left;
    align-items: center;
  }
`;

export default function RolesEdit({ user, setUser, selectedTeam }: Props) {
  const orgTreeDatabase = useContext(OrgTreeContext);
  const userContext = useContext(CurrentUserContext);
  const [userRoles, setUserRoles] = useState<RoleOption[]>([]);

  useEffect(() => {
    const newUserRoles: RoleOption[] = [];
    user.PCRoles.forEach((role) => {
      newUserRoles.push({
        name: getDropdoneValue(role.role).name,
        code: role.role,
      });
    });
    setUserRoles(newUserRoles);
  }, [user, orgTreeDatabase]);

  const getNodeFromTeamId = (teamId: string) => {
    if (orgTreeDatabase) {
      const teamData = findNodeById([orgTreeDatabase], teamId);
      if (teamData) {
        return teamData;
      }
    }
  };

  const rolesCampusAdmin = [
    { name: "Reporting Volunteer", code: "volunteer" },
    { name: "Reporting Church Staff", code: "churchStaff" },
    { name: "Reporting Pastoral Care Staff", code: "pastoralCare" },
    { name: "Campus Pastor", code: "campusPastor" },
    { name: "Oversight", code: "oversight" },
    { name: "Admin", code: "admin" },
  ];

  const rolesCampusOversight = [
    { name: "Reporting Volunteer", code: "volunteer" },
    { name: "Reporting Church Staff", code: "churchStaff" },
    { name: "Reporting Pastoral Care Staff", code: "pastoralCare" },
    { name: "Campus Pastor", code: "campusPastor" },
    { name: "Oversight", code: "oversight" },
  ];

  const rolesCampus = userContext?.currentUser?.PCRoles.some(
    (r) => r.role === "admin"
  )
    ? rolesCampusAdmin
    : rolesCampusOversight;

  const handleRoleChange = (e: any, index: number) => {
    // Deep copy the user object
    const updatedUser: UserData = JSON.parse(JSON.stringify(user));

    // Update the role of the user based on the index and the new value from the dropdown
    if (updatedUser.PCRoles && updatedUser.PCRoles[index]) {
      updatedUser.PCRoles[index].role = e.value;
    }

    // Call setUser with the updated user object
    setUser(updatedUser);
  };

  function getDropdoneValue(role: string): RoleOption {
    const roleData = rolesCampus.find((r) => r.code === role);
    if (roleData) {
      return roleData;
    }
    return { name: "", code: "" };
  }
  const dropdownRoles = rolesCampus.map((role) => ({
    label: role.name,
    value: role.code,
  }));

  const addRole = () => {
    const updatedUser: UserData = JSON.parse(JSON.stringify(user));
    updatedUser.PCRoles.push({
      role: "volunteer",
      teamId: selectedTeam?.teamId || "",
    });
    setUser(updatedUser);
  };

  const onRoleDelete = (e: any) => {
    const index = e.target.id.split("-")[2];
    const updatedUser = JSON.parse(JSON.stringify(user));
    console.log(updatedUser)
    updatedUser.PCRoles.splice(index, 1);
    setUser(updatedUser);
  };

  const roleTeamChange = (e: any, index: number) => {
    const updatedUser: UserData = JSON.parse(JSON.stringify(user));

    if (updatedUser.PCRoles && updatedUser.PCRoles[index]) {
      updatedUser.PCRoles[index].teamId = e.teamId;
    }
    setUser(updatedUser);
  };

  //check if the currentUse has sufficiant access level to show the role.
  function showRole(role: UserRole) {
    if (!userContext?.currentUser || !orgTreeDatabase) return;
    const userAdminTree = createAdminOrgTree(
      userContext.currentUser,
      orgTreeDatabase
    );
    const userAdminNode = findNodeById(userAdminTree, role.teamId);
    if (!userAdminNode) return false;
    if (userAdminNode) return true;
  }

  return (
    <RolesEditWrapper>
      {user.PCRoles.length === 0 && <p>Role(s)</p>}
      {user.PCRoles &&
        user.PCRoles.length > 0 &&
        user.PCRoles.map((role, index) => {
          if (!showRole(role)) return;
          return (
            <div key={`role-container-${index}`}>
              {index > 0 && <Line />}
              <p key={`role-number-${index}`}>Role {index + 1}</p>
              <div className="role-box-edit" key={`role-box-edit-${index}`}>
                <Dropdown
                  key={`role-drop-${index}`}
                  value={userRoles[index] ? userRoles[index].code : ""}
                  onChange={(e) => handleRoleChange(e, index)}
                  options={dropdownRoles}
                  placeholder="Select a Role"
                />
                <i
                  onClick={onRoleDelete}
                  id={`role-id-${index}`}
                  key={`role-delete-${index}`}
                  className="pi pi-times"
                  style={{ color: "red" }}
                ></i>
              </div>
              <p key={`team-role-number-${index}`}>Team Role {index + 1}</p>
              <div className="role-box-edit" key={`role-box-edit-2-${index}`}>
                <OrgTreeSelect
                  key={`team-drop-${index}`}
                  selectedTeam={getNodeFromTeamId(role.teamId)}
                  teamSelect={(e) => roleTeamChange(e, index)}
                />
              </div>
            </div>
          );
        })}
      <p className="add-role" onClick={addRole}>
        Add Role
        <i
          key={`index plus role`}
          className="pi pi-plus"
          style={{
            color: "green",
            fontSize: "7px",
            fontWeight: "bold",
            marginLeft: "3px",
          }}
        ></i>
      </p>
    </RolesEditWrapper>
  );
}

interface Props {
  user: UserData;
  selectedTeam?: OrgTreeItem;
  setUser: (user: UserData) => void;
}

interface RoleOption {
  name: string;
  code: string;
}

{
  /* <p>Team Leader</p>
<InputSwitch
  checked={checked}
  onChange={(e) => setChecked(!!e.value)}
  key={`leader-switch-id-${user.userId}`}
/> */
}
